/**
 * Suggests a badge color that is as different as possible from the colors
 * already in use (tags in a module / projects in a company).
 *
 * Strategy: colors are compared by hue on the HSL wheel. The suggestion lands
 * in the middle of the widest hue gap between used colors, which both
 * maximizes the distance to every existing color and keeps successive
 * suggestions maximally spread out (each new color halves the widest gap, so
 * the next suggestion moves to the next-widest one).
 *
 * Near-grays (very low saturation) carry no usable hue and are ignored.
 */

const DEFAULT_SATURATION = 0.65;
const DEFAULT_LIGHTNESS = 0.55;

/** First suggestion when nothing is used yet. */
const FALLBACK = '#4f46e5';

function hexToHsl(hex: string): { h: number; s: number; l: number } | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 0xff) / 255;
  const g = ((n >> 8) & 0xff) / 255;
  const b = (n & 0xff) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return { h: 0, s: 0, l };
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return { h: h * 360, s, l };
}

function hslToHex(h: number, s: number, l: number): string {
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const c = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(c * 255)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

export function suggestColor(usedColors: ReadonlyArray<string | null | undefined>): string {
  const hues = usedColors
    .map((c) => (c ? hexToHsl(c) : null))
    .filter((hsl): hsl is NonNullable<typeof hsl> => hsl !== null && hsl.s >= 0.15)
    .map((hsl) => hsl.h)
    .sort((a, b) => a - b);

  if (hues.length === 0) return FALLBACK;

  // Widest circular gap between consecutive hues (incl. wrap-around).
  let gapStart = hues[hues.length - 1];
  let gapSize = hues[0] + 360 - gapStart;
  for (let i = 1; i < hues.length; i++) {
    const size = hues[i] - hues[i - 1];
    if (size > gapSize) {
      gapSize = size;
      gapStart = hues[i - 1];
    }
  }

  const hue = (gapStart + gapSize / 2) % 360;
  return hslToHex(hue, DEFAULT_SATURATION, DEFAULT_LIGHTNESS);
}
